import type { FiltersType } from '../components/filters/filters'
import type { TextAlign } from 'chart.js'

import cx from 'classnames'
import React from 'react'

import { NoDataPlaceHolder } from '../../PatientFolder/behavioral/components'
import { Filters } from '../components/filters/filters'
import { Pagination } from '../components/pagination'

import { DelayedLoading } from './delayed-loading'
import { Header } from './header'
import { TableHead } from './table-head'
import styles from './table.module.css'

export let formatterPrice = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

export interface WithId {
  id: string | number
}

export interface Sort<T extends Item>{ on: keyof T, dir: 'asc' | 'desc' }

export type Item = WithId & Record<string, string | string[] | number | null>

export type TableSchema<T extends Item> = Array<ItemSchema<T> | ComponentSchema<T>>

interface ComponentSchema<T extends Item> {
  label: string
  getComponent: (item: T) => JSX.Element | null
  gridArea?: number
  alignment?: TextAlign
}

export interface ItemSchema<T extends Item> {
  name: keyof T
  label: string
  getValue?: (item: T) => string | number | null
  gridArea?: number
  unifying?: boolean
  alignment?: TextAlign
  sort?: boolean | string
}

interface TableProps<T extends Item> {
  schema: TableSchema<T>
  loading?: boolean
  rows: T[] | null
  title: string
  pagination: { total?: number, count: number, page: number }
  onCountChange: (value: number) => void
  onPaginationChange: (value: number) => void
  onSortChange: (on: string) => void
  sort: Sort<T>
  filters?: FiltersType
  search?: string
  onSearchChange?: (value: string) => void
  onFilterChange?: (key: keyof FiltersType, value: string | number | null) => void
}

export function Table<T extends Item> ({
  schema,
  loading = false,
  rows,
  title,
  pagination,
  onCountChange,
  onPaginationChange,
  onSortChange,
  sort,
  onFilterChange,
  search,
  filters,
  onSearchChange
}: TableProps<T>): JSX.Element | null {
  function getValueFromItem (itemSchema: ItemSchema<T>, item?: T): string | number | null {
    if (!item) {
      return null
    }

    let itemValue = item[itemSchema.name]

    let value = itemSchema.getValue?.(item) ??
      (Array.isArray(itemValue)
        ? itemValue?.join(', ')
        : itemValue)

    return value ?? null
  }

  let grid = schema.map((schemaItem) => `${schemaItem.gridArea ?? 1}fr`).join(' ')

  return (
    <TableWrapper
      search={search}
      filters={filters}
      onSearchChange={onSearchChange}
      onFilterChange={onFilterChange}
      title={title}
      onCountChange={onCountChange}
      onPaginationChange={onPaginationChange}
      pagination={pagination}
    >
      {rows && !rows.length && !loading
        ? <NoDataPlaceHolder title='No data' />
        : (
          <>
            <TableHead
              grid={grid}
              handleSortChange={onSortChange}
              sort={sort}
              schema={schema}
            />
            <div className={styles.tableWrapper}>
              <DelayedLoading loading={loading} />

              {rows?.map((item, key, array) => (
                <div
                  className={styles.tableRowContainer}
                  style={{ gridTemplateColumns: grid }}
                  key={item.id}
                >
                  {schema.map((schemaItem) => {
                    if ('getComponent' in schemaItem) {
                      return schemaItem.getComponent(item)
                    }
                    let { name, alignment } = schemaItem
                    let value = getValueFromItem(schemaItem, item)

                    if (key !== 0 && schemaItem.unifying && value === getValueFromItem(schemaItem, array[key - 1])) {
                      return <div />
                    }

                    return (
                      <ValueOrNull
                        value={value ?? null}
                        alignment={alignment}
                        key={name as string}
                      />
                    )
                  })}
                </div>
              ))}
            </div>
          </>
        )}
    </TableWrapper>
  )
}

interface ValueOrNullProps {
  value: string | number | null
  alignment?: TextAlign
}

function ValueOrNull ({ value, alignment }: ValueOrNullProps): JSX.Element {
  return (
    <p
      className={cx({
        [styles.valueIsNull as string]: value === null
      })}
      style={{ textAlign: alignment }}
    >
      {value ?? '—'}
    </p>
  )
}

interface TableWrapperProps {
  children: React.ReactNode
  filters?: FiltersType
  search?: string
  onSearchChange?: (value: string) => void
  onFilterChange?: (key: keyof FiltersType, value: string | number | null) => void
  title: string
  pagination: { total?: number, count: number, page: number }
  onCountChange: (value: number) => void
  onPaginationChange: (value: number) => void
}

function TableWrapper ({ children, search, filters, onSearchChange, onFilterChange, title, pagination, onPaginationChange, onCountChange }: TableWrapperProps): JSX.Element {
  return (
    <div className={styles.tableContainer}>
      <div style={{ padding: '8px' }}>
        <div className={styles.headerRow}>
          <Header title={title} />

          {filters && (
            <Filters
              search={search}
              filters={filters}
              onSearchChange={onSearchChange}
              onFilterChange={onFilterChange}
            />
          )}
        </div>

        {children}
      </div>
      <Pagination
        page={pagination.page}
        total={pagination?.total}
        pageSize={pagination?.count}
        onPageChange={onPaginationChange}
        onPageSizeChange={onCountChange}
      />
    </div>
  )
}
